// caseSlice.js - Redux Toolkit slice for case management
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { caseAPI } from "./api";
import { logout } from "../auth/index";
import { createMiddleware } from "../middlewareUtil";

// Initial state (based on the original reducer)
const initialState = {
  creating: false,
  emptyingTrash: false,
  loading: false,
  loaded: false,
  updating: false,
  case: {},
};

export const loadCase = createAsyncThunk(
  "case/loadCase",
  async (caseId, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await caseAPI.loadCase(accessToken, caseId);

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to load case");
  }
);

export const createCase = createAsyncThunk(
  "case/createCase",
  async (caseData, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await caseAPI.createCase(accessToken, caseData);

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to create case");
  }
);

export const updateMetadata = createAsyncThunk(
  "case/updateMetadata",
  async (caseData, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await caseAPI.updateMetadata(accessToken, caseData);

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to update case metadata");
  }
);

export const deleteCase = createAsyncThunk(
  "case/deleteCase",
  async (caseData, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await caseAPI.deleteCase(accessToken, caseData);

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to delete case");
  }
);

export const emptyTrash = createAsyncThunk(
  "case/emptyTrash",
  async (caseData, { getState, dispatch, rejectWithValue }) => {
    const accessToken = getState().auth.accessCode;

    const result = await caseAPI.emptyTrash(accessToken, caseData);

    if (result.success) {
      return result.data;
    } else if (result.error === "unauthorized") {
      dispatch(logout());
      return rejectWithValue("Authentication failed");
    }

    return rejectWithValue("Failed to empty trash");
  }
);

// Create the slice
const caseSlice = createSlice({
  name: "case",
  initialState,
  reducers: {
    reset: () => cloneDeep(initialState),
  },
  extraReducers: (builder) => {
    builder
      // Load case
      .addCase(loadCase.pending, (state, _action) => {
        state.creating = false;
        state.updating = false;
        state.loading = true;
        state.loaded = false;
      })
      .addCase(loadCase.fulfilled, (state, action) => {
        state.creating = false;
        state.updating = false;
        state.loading = false;
        state.loaded = true;
        state.case = action.payload;
      })
      .addCase(loadCase.rejected, (state, _action) => {
        state.creating = false;
        state.updating = false;
        state.loading = false;
        state.loaded = true;
        state.case = {};
      })

      // Create case
      .addCase(createCase.pending, (state, _action) => {
        state.creating = true;
        state.loading = true;
        state.loaded = false;
        state.case = {};
      })
      .addCase(createCase.fulfilled, (state, action) => {
        state.creating = false;
        state.loading = false;
        state.loaded = true;
        state.case = action.payload;
      })
      .addCase(createCase.rejected, (state, _action) => {
        state.creating = false;
        state.loading = false;
        state.loaded = true;
        state.case = {};
      })

      // Update metadata
      .addCase(updateMetadata.pending, (state, _action) => {
        state.updating = true;
        state.loading = true;
        state.loaded = false;
      })
      .addCase(updateMetadata.fulfilled, (state, action) => {
        state.creating = false;
        state.updating = false;
        state.loading = false;
        state.loaded = true;
        state.case = action.payload;
      })
      .addCase(updateMetadata.rejected, (state, _action) => {
        state.creating = false;
        state.updating = false;
        state.loading = false;
        state.loaded = true;
        state.case = {};
      })

      // Delete case - resets state after successful deletion
      .addCase(deleteCase.fulfilled, (_state, _action) => {
        return cloneDeep(initialState);
      })
      .addCase(deleteCase.rejected, (_state, _action) => {
        // Reset state even on error as per original implementation
        return cloneDeep(initialState);
      })

      // Empty trash - pending state
      .addCase(emptyTrash.pending, (state, _action) => {
        state.emptyingTrash = true;
      })

      // Empty trash - resets state after successful operation
      .addCase(emptyTrash.fulfilled, (_state, _action) => {
        return cloneDeep(initialState);
      })
      .addCase(emptyTrash.rejected, (_state, _action) => {
        // Reset state even on error as per original implementation
        return cloneDeep(initialState);
      });
  },
});

export const caseMiddleware = createMiddleware([
  "case/loadCase/pending",
  "case/createCase/pending",
  "case/emptyTrash/pending",
  "case/updateMetadata/pending",
]);

// Export actions
export const { reset } = caseSlice.actions;

// Export reducer
export default caseSlice.reducer;
